import { useState, useEffect, useRef } from "react";
import { GetUserSubscription } from "../../Api/Subscription";
import { SubscriptionDTO } from "../../Models/DTOs/Subscription";

type UseFetchUserSubscriptionArgs = {
    getValidAccessToken: () => Promise<string | null>;
    userId: string | null | undefined;
};

function useFetchUserSubscription({ getValidAccessToken, userId }: UseFetchUserSubscriptionArgs) {
    const [subscriptionData, setSubscriptionData] = useState<SubscriptionDTO | null>(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(false);
    const [subscriptionError, setSubscriptionError] = useState<string>("");
    const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);

    // Keep the latest token getter in a ref so the effect can use it without
    // depending on it — the context recreates the function on every render, and
    // depending on it would abort + refetch on each provider re-render.
    const getTokenRef = useRef(getValidAccessToken);
    getTokenRef.current = getValidAccessToken;

    useEffect(() => {
        let isCancelled = false;
        const abortController = new AbortController();

        // reset on userId change
        setSubscriptionError("");
        setSubscriptionData(null);
        setResolvedUserId(null);

        if (!userId || !userId.trim()) {
            setSubscriptionLoading(false);
            return () => {
                abortController.abort();
                isCancelled = true;
            };
        }

        setSubscriptionLoading(true);

        (async () => {
            try {
                // GET /v1/users/{userId}/subscription now requires a bearer token.
                const token = await getTokenRef.current();
                if (isCancelled) return;

                if (!token) {
                    setSubscriptionError("No valid access token available.");
                    return;
                }

                const res = await GetUserSubscription(userId, token, abortController);
                if (isCancelled) return;

                // AxiosError case (our API helper returns it instead of throwing)
                if ((res as any)?.isAxiosError) {
                    setSubscriptionError("Failed to fetch subscription data.");
                    return;
                }

                // res is either SubscriptionDTO or null
                setSubscriptionData(res as SubscriptionDTO | null);
            } catch (e: any) {
                if (!isCancelled) {
                    setSubscriptionError(e?.message || "Failed to fetch subscription data.");
                }
            } finally {
                if (!isCancelled) {
                    setSubscriptionLoading(false);
                    setResolvedUserId(userId);
                }
            }
        })();

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [userId]);

    return {
        subscriptionData,
        subscriptionLoading,
        subscriptionError,
        subscriptionResolved: !userId || resolvedUserId === userId,
    };
}

export default useFetchUserSubscription;
